<template>
  <div class="product-detail-page">
    <div class="header">
      <button @click="goBack" class="back-btn">← 返回列表</button>
      <h2>{{ product.name || '物品详情' }}</h2>
      <button @click="goSku" class="sku-btn" v-if="product.id">SKU 管理</button>
    </div>

    <div v-if="loading" class="loading-text">加载中...</div>

    <template v-else>
      <!-- 基本信息 -->
      <div class="info-card">
        <div class="card-header">
          <h3>物品基本信息</h3>
        </div>
        <div class="card-body">
          <div class="detail-top">
            <img :src="product.image || DEFAULT_NO_IMAGE" class="detail-img" />
            <div class="info-grid">
              <div class="info-item">
                <label>物品ID</label>
                <span>{{ product.id }}</span>
              </div>
              <div class="info-item">
                <label>物品名称</label>
                <span>{{ product.name }}</span>
              </div>
              <div class="info-item">
                <label>分类</label>
                <span>{{ product.category ? product.category.name : '未分类' }}</span>
              </div>
              <div class="info-item">
                <label>所属店家</label>
                <span>{{ shopName }}</span>
              </div>
              <div class="info-item">
                <label>是否热门</label>
                <span :class="'status ' + (product.is_hot === 1 ? 'hot' : '')">{{ product.is_hot === 1 ? '是' : '否' }}</span>
              </div>
              <div class="info-item">
                <label>是否新品</label>
                <span>{{ product.is_new === 1 ? '是' : '否' }}</span>
              </div>
              <div class="info-item">
                <label>是否推荐</label>
                <span>{{ product.is_recommend === 1 ? '是' : '否' }}</span>
              </div>
              <div class="info-item">
                <label>销量</label>
                <span>{{ product.sales_count || 0 }}</span>
              </div>
              <div class="info-item">
                <label>收藏数</label>
                <span>{{ product.collect_count || 0 }}</span>
              </div>
              <div class="info-item">
                <label>浏览数</label>
                <span>{{ product.browse_count || 0 }}</span>
              </div>
              <div class="info-item">
                <label>排序</label>
                <span>{{ product.sort_order }}</span>
              </div>
              <div class="info-item">
                <label>状态</label>
                <span :class="['status', product.status === 1 ? 'is-on' : 'is-off']">{{ product.status === 1 ? '上架' : '下架' }}</span>
              </div>
              <div class="info-item">
                <label>创建时间</label>
                <span>{{ formatDate(product.created_at) }}</span>
              </div>
              <div class="info-item">
                <label>更新时间</label>
                <span>{{ formatDate(product.updated_at) }}</span>
              </div>
            </div>
          </div>
          <div class="desc-block" v-if="product.description">
            <label>物品描述</label>
            <p class="desc-text">{{ product.description }}</p>
          </div>
        </div>
      </div>

      <!-- SKU 列表 -->
      <div class="info-card sku-card">
        <div class="card-header">
          <h3>SKU 列表 <span class="sku-count">共[ {{ skus.length }} ]个</span></h3>
        </div>
        <div class="card-body">
          <table v-if="skus.length" class="sku-table">
            <thead>
              <tr>
                <th>SKU ID</th>
                <th>图片</th>
                <th>规格</th>
                <th>货号</th>
                <th>单位</th>
                <th>参考售价</th>
                <th>搬运费</th>
                <th>楼层费</th>
                <th>销量</th>
                <th>默认</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sku in skus" :key="sku.id">
                <td>{{ sku.id }}</td>
                <td>
                  <img :src="sku.image || DEFAULT_NO_IMAGE" class="sku-img" />
                </td>
                <td>{{ skuSpecNameWithSpec(sku) }}</td>
                <td>{{ sku.code || '-' }}</td>
                <td>{{ sku.unit || '-' }}</td>
                <td>{{ formatYuan(sku.sale_price) }}</td>
                <td>{{ formatYuan(sku.carry_fee) }}</td>
                <td>{{ formatYuan(sku.floor_fee) }}</td>
                <td>{{ sku.sales_count || 0 }}</td>
                <td>{{ sku.is_default === 1 ? '是' : '否' }}</td>
                <td><span :class="['status', sku.status === 1 ? 'is-on' : 'is-off']">{{ sku.status === 1 ? '上架' : '下架' }}</span></td>
                <td>
                  <button type="button" class="sku-action-btn" @click="goSku">SKU 管理</button>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="empty-text">该物品暂无 SKU</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { getProductDetail, getSkuList } from '@/api/products'
import { DEFAULT_NO_IMAGE } from '@/constants'
import { getShopList } from '@/api/shops'
import { getSpecValueOptions, getSpecOptions, getSkuSpecs } from '@/api/specs'
import { buildSkuNameWithSpec, skuSpecListToValueIds } from '@/utils/skuSpec'

export default {
  name: 'ProductDetail',
  data() {
    return {
      productId: null,
      product: {},
      skus: [],
      shopOptions: [],
      specValueMap: {},
      specNameMap: {},
      loading: true
    }
  },
  computed: {
    DEFAULT_NO_IMAGE() {
      return DEFAULT_NO_IMAGE
    },
    shopName() {
      if (!this.product.shop_id) return '-'
      const shop = this.shopOptions.find(s => s && s.id === this.product.shop_id)
      if (!shop) return '店家#' + this.product.shop_id
      return (shop.community ? shop.community.name + ' - ' : '') + shop.name
    }
  },
  mounted() {
    this.productId = this.$route.params.id
    this.loadShops()
    this.loadProductDetail()
    this.loadSkus()
  },
  methods: {
    goBack() {
      this.$router.push('/products/list')
    },
    goSku() {
      this.$router.push({ name: 'productSku', query: { productId: this.product.id } })
    },
    async loadProductDetail() {
      try {
        const response = await getProductDetail(this.productId)
        if (response && response.data) {
          this.product = response.data
        }
      } catch (error) {
      } finally {
        this.loading = false
      }
    },
    async loadSkus() {
      try {
        const response = await getSkuList(this.productId)
        if (response && response.data) {
          const data = response.data
          this.skus = Array.isArray(data) ? data : (data.data || [])
          await this.loadSkuSpecs()
        }
      } catch (error) {
      }
    },
    // 拉取规格值映射，并为每个 SKU 加载其已选规格值用于展示名称
    async loadSkuSpecs() {
      try {
        const svRes = await getSpecValueOptions()
        const list = (svRes && svRes.data && svRes.data.spec_values) || []
        const map = {}
        list.forEach(v => { map[v.id] = v })
        this.specValueMap = map
        const spRes = await getSpecOptions()
        const specs = (spRes && spRes.data && spRes.data.specs) || []
        const specNameMap = {}
        specs.forEach(s => { if (s && s.id != null) specNameMap[s.id] = s.name })
        this.specNameMap = specNameMap
      } catch (e) {
        this.specValueMap = {}
        this.specNameMap = {}
      }
      await Promise.all(this.skus.map(async (sku) => {
        try {
          const res = await getSkuSpecs(sku.id)
          const items = (res && res.data && res.data.list) || []
          this.$set(sku, '_specValueIds', skuSpecListToValueIds(items))
        } catch (e) {
          this.$set(sku, '_specValueIds', [])
        }
      }))
    },
    skuSpecNameWithSpec(sku) {
      const name = buildSkuNameWithSpec(sku._specValueIds || [], this.specValueMap, this.specNameMap)
      return name || '-'
    },
    async loadShops() {
      try {
        const response = await getShopList({ page: 1 })
        if (response && response.data) {
          const pageData = response.data
          const items = pageData.data || pageData
          this.shopOptions = Array.isArray(items) ? items : []
        }
      } catch (error) {
      }
    },
    formatYuan(fen) {
      const val = Number(fen || 0)
      return '' + (val / 100).toFixed(2)
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.product-detail-page {
  padding: 20px;
}

.header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  font-size: 20px;
  color: #333;
  flex: 1;
}

.back-btn {
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.back-btn:hover {
  background: #f5f5f5;
}

.sku-btn {
  padding: 8px 16px;
  background: #9b59b6;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.loading-text {
  padding: 40px 0;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.info-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
}

.card-header {
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
}

.card-header h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.sku-count {
  margin-left: 8px;
  padding: 1px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #9b59b6;
  background: #f5eef8;
  border-radius: 10px;
  vertical-align: middle;
}

.card-body {
  padding: 20px;
}

.detail-top {
  display: flex;
  gap: 20px;
}

.detail-img {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #eee;
  flex-shrink: 0;
}

.detail-img.no-image {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 13px;
  background: #fafafa;
}

.info-grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.info-item {
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  background: #fafafa;
  border-radius: 6px;
}

.info-item label {
  font-size: 13px;
  color: #999;
  margin-bottom: 5px;
}

.info-item span {
  font-size: 14px;
  color: #333;
}

.desc-block {
  margin-top: 20px;
  padding: 16px;
  background: #fafafa;
  border-radius: 6px;
}

.desc-block label {
  display: block;
  font-size: 13px;
  color: #999;
  margin-bottom: 8px;
}

.desc-text {
  margin: 0;
  font-size: 14px;
  color: #333;
  line-height: 1.6;
  white-space: pre-wrap;
}

.sku-card {
  margin-top: 20px;
}

.sku-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.sku-table th,
.sku-table td {
  padding: 12px 14px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
}

.sku-table th {
  background: #fafafa;
  color: #666;
  font-weight: 600;
}

.sku-table tbody tr:hover {
  background: #fafafa;
}

.sku-img {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 4px;
}

.sku-action-btn {
  padding: 5px 12px;
  background: #9b59b6;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.sku-action-btn:hover {
  background: #8e44ad;
}

.no-image {
  color: #999;
}

.status {
  align-self: flex-start;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
}

.status.hot { background: #fff8e1; color: #f39c12; }
.status.is-on { background: #e8f5e9; color: #2ecc71; }
.status.is-off { background: #ffebee; color: #e74c3c; }

.empty-text {
  padding: 30px 0;
  text-align: center;
  color: #999;
  font-size: 14px;
}
</style>
